import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { HtmlDoc } from 'shared/utils/html-doc';
import { AudioPlayer } from './AudioPlayer';
import { GENERATE_TEXT_AUDIO } from '../../../graphql/modules/text-to-speech';
import { draftToHtml } from 'utils/helpers';
import { EditAudioButton } from './EditAudioButton';
import styles from './AudioToolbar.less';

export const AudioToolbar = (props) => {
  const { value, draftEditorState: editorState, learningSubject, onAudioChange } = props;

  const [pureText, setPureText] = useState(() => {
    if (!value?.html) {
      return '';
    }

    const htmlDoc = HtmlDoc.fromPartialHtml(value.html);
    return htmlDoc.getPureText();
  });

  const [url, setUrl] = useState(value?.audioUrl);

  const [generateAudioUrl] = useMutation(GENERATE_TEXT_AUDIO);

  const generateTextAudio = async (text) => {
    const { data } = await generateAudioUrl({
      variables: { text, learningSubject },
    });

    return data.generateTextAudio;
  };

  const handleAudioChange = (pureText, audioPayload) => {
    const { audioFileKey, audioFileUrl } = audioPayload;
    setUrl(audioFileUrl);
    setPureText(pureText);
    onAudioChange({ audioFileKey, audioFileUrl });
  };

  const handleStartPlaying = async () => {
    const currentPureText = getPureText(editorState);

    if (currentPureText === null) {
      return;
    }

    if (url && pureText === currentPureText) {
      return;
    }

    setUrl(null);

    const { fileUrl: audioFileUrl, fileKey: audioFileKey } = await generateTextAudio(
      currentPureText
    );

    handleAudioChange(currentPureText, { audioFileUrl, audioFileKey });
  };

  return (
    <div className={classNames('rdw-image-wrapper', styles.audioWrapper)}>
      <AudioPlayer url={url ?? ''} onStart={handleStartPlaying} />
      <EditAudioButton
        text={getPureText(editorState)}
        generateTextAudio={generateTextAudio}
        onChange={handleAudioChange}
      />
    </div>
  );
};

AudioToolbar.propTypes = {
  value: PropTypes.shape({
    html: PropTypes.string.isRequired,
    audioFileKey: PropTypes.string,
    audioUrl: PropTypes.string,
  }),
  draftEditorState: PropTypes.object.isRequired,
  learningSubject: PropTypes.string,
  onAudioChange: PropTypes.func.isRequired,
};

const getPureText = (editorState) => {
  if (!editorState) {
    return null;
  }

  const html = draftToHtml(editorState);
  const htmlDoc = HtmlDoc.fromPartialHtml(html);
  return htmlDoc.getPureText();
};
